import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Déclaration pour la fonction de préchargement
declare global {
  interface Window {
    preloadPollCreator?: () => void;
  }
}

import {
  Calendar,
  ClipboardList,
  MessageSquare,
  Users,
  Vote,
  BarChart3,
  Trash2,
  Check,
  MoreVertical,
  Settings,
  Share2,
  Download,
  Edit,
  Copy,
  Lock,
  Archive,
} from "lucide-react";
import { ConversationItem, DashboardPoll } from "./types";
import { getStatusColor, getStatusLabel, getThemeColors } from "./utils";
import { useConversations } from "@core/hooks/useConversations";
import { useToast } from "@core/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@core/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@core/components/ui/alert-dialog";
import { Button } from "@core/components/ui/button";
import {
  addPoll,
  buildPublicLink,
  copyToClipboard,
  deleteVotesByPollId,
  Poll, // Add missing Poll import
} from "@core/lib/pollStorage";
import type { UnifiedPoll } from "@core/lib/pollStorageUnified";
import { pollService } from "@core/services/PollService";
import {
  exportFormPollToCSV,
  exportFormPollToPDF,
  exportFormPollToJSON,
  exportFormPollToMarkdown,
  hasExportableData,
} from "@core/lib/exports";
import { usePollDeletionCascade } from "@core/hooks/usePollDeletionCascade";
import { createConversationForPoll } from "@core/lib/ConversationPollLink";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { ManageTagsFolderDialog } from "./ManageTagsFolderDialog";

interface DashboardTableViewProps {
  items: ConversationItem[];
  selectedIds: Set<string>;
  onToggleSelection: (id: string) => void;
  onRefresh: () => void;
}

export const DashboardTableView: React.FC<DashboardTableViewProps> = ({
  items,
  selectedIds,
  onToggleSelection,
  onRefresh,
}) => {
  console.log("📋 [DASHBOARD_TABLE] Rendering DashboardTableView", {
    itemsCount: items?.length || 0,
    selectedIdsCount: selectedIds.size,
  });

  const navigate = useNavigate();
  const { t } = useTranslation("polls");
  const { toast } = useToast();
  const { deleteConversation } = useConversations();
  const { deletePollWithCascade } = usePollDeletionCascade();
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [showTagsFolderDialog, setShowTagsFolderDialog] = useState<
    string | null
  >(null);
  const [copiedPollIds, setCopiedPollIds] = useState<Set<string>>(new Set());
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);
  const [pollToDuplicate, setPollToDuplicate] = useState<Poll | null>(null);
  const preloadTimeoutRefs = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleDeleteConversation = async (itemId: string) => {
    if (!window.confirm(t("dashboard.conversation_delete_confirm"))) {
      return;
    }

    setDeletingIds((prev) => new Set(prev).add(itemId));
    try {
      await deleteConversation.mutateAsync(itemId);
      toast({
        title: t("dashboard.conversation_deleted"),
        description: t("dashboard.conversation_deleted_desc"),
      });
      onRefresh();
    } catch (error) {
      toast({
        title: t("dashboard.error"),
        description: t("dashboard.conversation_delete_error"),
        variant: "destructive",
      });
    } finally {
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Handlers pour les actions de poll (extraits de PollActions)
  const handleCopyLink = async (poll: DashboardPoll) => {
    try {
      const url = buildPublicLink(poll.slug);
      await copyToClipboard(url);
      setCopiedPollIds((prev) => new Set(prev).add(poll.id));
      toast({
        title: t("poll_actions.link_copied"),
        description: t("poll_actions.link_copied_desc"),
      });
      setTimeout(() => {
        setCopiedPollIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(poll.id);
          return newSet;
        });
      }, 2000);
    } catch {
      toast({
        title: t("dashboard.error"),
        description: t("poll_actions.link_copy_error"),
        variant: "destructive",
      });
    }
  };

  const handlePreloadEdit = (poll: DashboardPoll) => {
    // Précharger PollCreator si c'est un sondage de dates (pas formulaire)
    if (poll.type !== "form") {
      const timeoutRef = preloadTimeoutRefs.current.get(poll.id);
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
      const newTimeout = setTimeout(() => {
        if (typeof window.preloadPollCreator === "function") {
          window.preloadPollCreator();
        }
      }, 300);
      preloadTimeoutRefs.current.set(poll.id, newTimeout);
    }
  };

  const handleMouseLeaveEdit = (poll: Poll) => {
    const timeoutRef = preloadTimeoutRefs.current.get(poll.id);
    if (timeoutRef) {
      clearTimeout(timeoutRef);
      preloadTimeoutRefs.current.delete(poll.id);
    }
  };

  const handleEdit = (poll: Poll) => {
    if (poll.type === "form") {
      navigate(`/workspace/form?edit=${poll.id}`);
    } else {
      navigate(`/workspace/date?edit=${poll.id}`);
    }
  };

  const handleDuplicate = (poll: Poll) => {
    setPollToDuplicate(poll);
    setDuplicateConfirmOpen(true);
  };

  const confirmDuplicate = async () => {
    if (!pollToDuplicate) return;

    try {
      const dup = await pollService.duplicatePoll(pollToDuplicate as any);
      // Le poll est déjà créé dans Supabase via pollService.duplicatePoll()
      // Create conversation for date polls
      if (pollToDuplicate.type === "date") {
        createConversationForPoll(dup.id, dup.title, "date");
      }
      toast({
        title: t("poll_actions.duplicated"),
        description: t("poll_actions.duplicated_desc", { title: dup.title }),
      });
      setDuplicateConfirmOpen(false);
      setPollToDuplicate(null);
      onRefresh();
    } catch (error) {
      logError(error, {
        component: "DashboardTableView",
        operation: "duplicatePoll",
      });
      toast({
        title: t("dashboard.error"),
        description: t("poll_actions.duplicate_error"),
        variant: "destructive",
      });
    }
  };

  const handleArchive = async (poll: DashboardPoll) => {
    try {
      await pollService.updatePoll(poll.id, {
        status: "archived",
        updated_at: new Date().toISOString(),
      });
      // Mettre à jour le cache local pour refléter immédiatement le changement
      addPoll({
        ...poll,
        status: "archived",
        updated_at: new Date().toISOString(),
      } as any);
      toast({
        title: t("poll_actions.archived"),
        description: t("poll_actions.archived_desc"),
      });
      onRefresh();
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("pollArchived", { detail: { pollId: poll.id } }),
        );
      }
    } catch {
      toast({
        title: t("dashboard.error"),
        description: t("poll_actions.archive_error"),
        variant: "destructive",
      });
    }
  };

  const handleClose = (poll: import("../../../lib/pollStorage").Poll) => {
    if (!window.confirm(t("poll_actions.close_confirm"))) return;
    try {
      const updatedPoll = {
        ...poll,
        status: "closed" as const,
        updated_at: new Date().toISOString(),
      };
      addPoll(updatedPoll);
      toast({
        title: t("poll_actions.closed"),
        description: t("poll_actions.closed_desc"),
      });
      onRefresh();
    } catch {
      toast({
        title: t("dashboard.error"),
        description: t("poll_actions.close_error"),
        variant: "destructive",
      });
    }
  };

  const handleDeletePoll = async (poll: DashboardPoll, itemId: string) => {
    const confirmMessage = t("poll_actions.delete_confirm");

    if (!window.confirm(confirmMessage)) return;

    try {
      deleteVotesByPollId(poll.id);
      const result = await deletePollWithCascade(poll.id, {
        deleteConversation: true,
      });
      if (result.success) {
        toast({
          title: t("poll_actions.deleted"),
          description: t("poll_actions.deleted_desc"),
        });
        onRefresh();
      } else {
        throw ErrorFactory.storage(
          result.error || "Failed to delete poll",
          t("poll_actions.delete_error"),
        );
      }
    } catch (error) {
      logError(error, {
        component: "DashboardTableView",
        operation: "deletePoll",
      });
      toast({
        title: t("dashboard.error"),
        description:
          error instanceof Error
            ? error.message
            : t("poll_actions.delete_error"),
        variant: "destructive",
      });
    }
  };

  // Convert DashboardPoll to UnifiedPoll for exports (déplacé ici)
  // Note: sera créé dans chaque ligne du tableau
  const unifiedPoll = (poll: DashboardPoll) => ({
    id: poll.id,
    type: poll.type,
    title: poll.title,
    description: poll.description,
    slug: poll.slug,
    poll_data_json: poll as any, // DashboardPoll contient les données
    creator_id: poll.creator_id,
    creator_type: poll.creator_type,
    guest_fingerprint: poll.guest_fingerprint,
    guest_session_id: poll.guest_session_id,
    status: poll.status,
    created_at: poll.created_at,
    updated_at: poll.updated_at,
  });

  const handleExport = (
    poll: DashboardPoll,
    format: "csv" | "pdf" | "json" | "markdown",
  ) => {
    if (poll.type !== "form") {
      toast({
        title: t("export.not_supported"),
        description: t("export.not_supported_desc"),
        variant: "destructive",
      });
      return;
    }

    // Convert DashboardPoll to UnifiedPoll for exports
    const unifiedPollData = unifiedPoll(poll);

    try {
      switch (format) {
        case "csv":
          exportFormPollToCSV(unifiedPollData);
          toast({
            title: t("export.csv_success"),
            description: t("export.csv_success_desc"),
          });
          break;
        case "pdf":
          exportFormPollToPDF(unifiedPollData);
          toast({
            title: t("export.pdf_title"),
            description: t("export.pdf_desc"),
          });
          break;
        case "json":
          exportFormPollToJSON(unifiedPollData);
          toast({
            title: t("export.json_success"),
            description: t("export.json_success_desc"),
          });
          break;
        case "markdown":
          exportFormPollToMarkdown(unifiedPollData);
          toast({
            title: t("export.markdown_success"),
            description: t("export.markdown_success_desc"),
          });
          break;
      }
    } catch (err) {
      logError(
        err instanceof Error
          ? err
          : ErrorFactory.api("Export error", t("export.export_error")),
        {
          component: "DashboardTableView",
          operation: "handleExport",
          pollId: poll.id,
        },
      );
      toast({
        title: t("export.error_title"),
        description:
          err instanceof Error ? err.message : t("export.export_failed"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="overflow-x-auto" data-testid="dashboard-table-view">
      <table className="w-full border-collapse">
        <colgroup>
          <col style={{ width: "48px", minWidth: "48px" }} />
          <col />
          <col style={{ width: "100px", minWidth: "80px" }} />
          <col
            className="hidden lg:table-column"
            style={{ width: "120px", minWidth: "100px" }}
          />
          <col
            className="hidden lg:table-column"
            style={{ width: "100px", minWidth: "80px" }}
          />
          <col style={{ width: "140px", minWidth: "120px" }} />
        </colgroup>
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-3 px-2 md:px-3 lg:px-4 text-sm font-medium text-muted-foreground">
              <span className="sr-only">{t("dashboard.table.selection")}</span>
            </th>
            <th className="text-left py-3 px-2 md:px-3 lg:px-4 text-sm font-medium text-muted-foreground">
              {t("dashboard.table.title")}
            </th>
            <th className="text-left py-3 px-2 md:px-3 lg:px-4 text-sm font-medium text-muted-foreground">
              {t("dashboard.table.status")}
            </th>
            <th className="hidden lg:table-cell text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              {t("dashboard.table.stats")}
            </th>
            <th className="hidden lg:table-cell text-left py-3 px-4 text-sm font-medium text-muted-foreground">
              {t("dashboard.table.date")}
            </th>
            <th className="text-left py-3 px-2 md:px-3 lg:px-4 text-sm font-medium text-muted-foreground">
              {t("dashboard.table.actions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const isSelected = selectedIds.has(item.id);
            const isDeleting = deletingIds.has(item.id);
            const theme = getThemeColors(item.poll?.type || "date");

            return (
              <tr
                key={item.id}
                className={`border-b border-border hover:bg-accent transition-colors cursor-pointer ${
                  isSelected ? theme.selectionBg : ""
                } ${index % 2 === 0 ? "bg-transparent" : "bg-muted/30"}`}
                onClick={() => {
                  // Ouvre le bon workspace selon le type de poll.
                  // Les form-polls n'ont pas de conversation IA réutilisable :
                  // on ouvre l'éditeur via ?edit= (même cible que handleEdit),
                  // sinon on reprend la conversation date via ?conversationId=.
                  if (item.poll?.type === "form") {
                    navigate(`/workspace/form?edit=${item.poll.id}`);
                  } else {
                    navigate(`/workspace/date?conversationId=${item.id}`);
                  }
                }}
              >
                {/* Checkbox */}
                <td
                  className="py-3 px-2 md:px-3 lg:px-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                      isSelected
                        ? theme.checkbox
                        : "bg-muted border-input hover:border-primary/60"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelection(item.id);
                    }}
                  >
                    {isSelected && (
                      <Check className="w-3 h-3 text-primary-foreground" />
                    )}
                  </div>
                </td>

                {/* Titre */}
                <td className="py-3 px-2 md:px-3 lg:px-4">
                  <div className="flex items-start gap-2">
                    {item.poll ? (
                      item.poll.type === "form" ? (
                        <ClipboardList className="w-4 h-4 text-violet-400 flex-shrink-0 mt-0.5" /> // color-exempt
                      ) : item.poll.type === "availability" ? (
                        <Calendar className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" /> // color-exempt
                      ) : (
                        <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" /> // color-exempt
                      )
                    ) : (
                      <MessageSquare className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-foreground font-medium line-clamp-2 break-words">
                        {item.poll ? item.poll.title : item.conversationTitle}
                      </div>
                      {item.poll?.description && (
                        <div className="text-muted-foreground text-xs line-clamp-1 mt-1">
                          {item.poll.description}
                        </div>
                      )}
                      {/* Badge mobile pour le nombre de participants */}
                      {item.poll && (item.poll.participants_count || 0) > 0 && (
                        <div className="lg:hidden flex items-center gap-2 mt-2">
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
                            <Users className="w-3 h-3" />
                            {item.poll.participants_count}
                          </span>
                          {(item.poll.votes_count || 0) > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">
                              <Vote className="w-3 h-3" />
                              {item.poll.votes_count}
                            </span>
                          )}
                        </div>
                      )}
                      {item.hasAI && (
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded mt-1 ${theme.lightBadge}`}
                        >
                          💬 {t("dashboard.ai_badge")}
                        </span>
                      )}
                    </div>
                  </div>
                </td>

                {/* Statut */}
                <td className="py-3 px-2 md:px-3 lg:px-4">
                  {item.poll ? (
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        item.poll.status === "draft"
                          ? "bg-muted text-muted-foreground"
                          : item.poll.status === "active"
                            ? "bg-primary/10 text-primary"
                            : item.poll.status === "closed"
                              ? "bg-primary/10 text-primary"
                              : "bg-destructive/10 text-destructive"
                      }`}
                    >
                      {getStatusLabel(item.poll.status)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      {t("dashboard.conversation")}
                    </span>
                  )}
                </td>

                {/* Statistiques */}
                <td className="hidden lg:table-cell py-3 px-4">
                  {item.poll ? (
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{item.poll.participants_count || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Vote className="w-3 h-3" />
                        <span>{item.poll.votes_count || 0}</span>
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted-foreground text-xs">-</span>
                  )}
                </td>

                {/* Date */}
                <td className="hidden lg:table-cell py-3 px-4">
                  <div className="text-xs text-muted-foreground">
                    {item.conversationDate.toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </div>
                </td>

                {/* Actions */}
                <td
                  className="py-3 px-2 md:px-3 lg:px-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="flex items-center gap-1.5 md:gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {item.poll ? (
                      <>
                        {/* Actions principales : Résultats et Voter */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/poll/${item.poll!.slug}/results`);
                          }}
                          className="p-2 md:p-2 lg:p-1.5 bg-muted hover:bg-accent text-muted-foreground rounded transition-colors touch-manipulation"
                          title={t("poll_actions.results")}
                          aria-label={t("poll_actions.results_aria")}
                          data-testid="dashboardtableview-navigate"
                        >
                          <BarChart3 className="w-5 h-5 md:w-4 md:h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/poll/${item.poll!.slug}`);
                          }}
                          className={`p-2 md:p-2 lg:p-1.5 ${theme.primaryButton} text-white rounded transition-colors touch-manipulation`}
                          title={t("poll_actions.vote")}
                          aria-label={t("poll_actions.vote")}
                          data-testid="dashboardtableview-navigate"
                        >
                          <Vote className="w-5 h-5 md:w-4 md:h-4" />
                        </button>

                        {/* Menu avec toutes les autres actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="bg-muted text-muted-foreground p-2 md:p-2 lg:p-1.5 rounded hover:bg-accent touch-manipulation"
                              aria-label={t("poll_actions.actions_menu")}
                              data-testid="dashboardtableview-button"
                            >
                              <MoreVertical className="w-5 h-5 md:w-4 md:h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowTagsFolderDialog(item.id);
                              }}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              {t("dashboard.manage_tags_folder")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyLink(item.poll);
                              }}
                            >
                              {copiedPollIds.has(item.poll.id) ? (
                                <>
                                  <Check className="w-4 h-4 mr-2" />
                                  {t("poll_actions.link_copied")}
                                </>
                              ) : (
                                <>
                                  <Share2 className="w-4 h-4 mr-2" />
                                  {t("poll_actions.copy_link")}
                                </>
                              )}
                            </DropdownMenuItem>
                            {item.poll.type === "form" &&
                              hasExportableData(unifiedPoll(item.poll)) && (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <Download className="w-4 h-4 mr-2" />
                                    {t("export.export")}
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExport(item.poll, "csv");
                                      }}
                                    >
                                      📊 CSV
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExport(item.poll, "pdf");
                                      }}
                                    >
                                      📄 PDF
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExport(item.poll, "json");
                                      }}
                                    >
                                      🔧 JSON
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleExport(item.poll, "markdown");
                                      }}
                                    >
                                      📝 Markdown
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              )}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(item.poll);
                              }}
                              onMouseEnter={() => handlePreloadEdit(item.poll)}
                              onMouseLeave={() =>
                                handleMouseLeaveEdit(item.poll)
                              }
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              {t("poll_actions.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicate(item.poll);
                              }}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              {t("poll_actions.duplicate")}
                            </DropdownMenuItem>
                            {item.poll.status === "active" && (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClose(item.poll);
                                }}
                              >
                                <Lock className="w-4 h-4 mr-2" />
                                {t("poll_actions.close")}
                              </DropdownMenuItem>
                            )}
                            {item.poll.status !== "archived" && (
                              <DropdownMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleArchive(item.poll);
                                }}
                              >
                                <Archive className="w-4 h-4 mr-2" />
                                {t("poll_actions.archive")}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePoll(item.poll, item.id);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t("poll_actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    ) : (
                      <>
                        {/* Actions pour conversation seule */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/workspace/date?conversationId=${item.id}`,
                            );
                          }}
                          className={`px-3 py-2 md:px-3 md:py-2 lg:px-2 lg:py-1 ${theme.primaryButton} text-white text-xs rounded transition-colors touch-manipulation`}
                          data-testid="dashboardtableview-reprendre"
                        >
                          {t("dashboard.resume")}
                        </button>

                        {/* Menu pour gérer tags/dossier */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="bg-muted text-muted-foreground p-2 md:p-2 lg:p-1.5 rounded hover:bg-accent touch-manipulation"
                              aria-label={t("poll_actions.actions_menu")}
                              data-testid="dashboardtableview-button"
                            >
                              <MoreVertical className="w-5 h-5 md:w-4 md:h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowTagsFolderDialog(item.id);
                              }}
                            >
                              <Settings className="w-4 h-4 mr-2" />
                              {t("dashboard.manage_tags_folder")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(item.id);
                              }}
                              className="text-destructive focus:text-destructive"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t("poll_actions.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Dialog pour gérer tags et dossier */}
      {showTagsFolderDialog && (
        <ManageTagsFolderDialog
          conversationId={showTagsFolderDialog}
          currentTags={
            items.find((i) => i.id === showTagsFolderDialog)?.tags || []
          }
          currentFolderId={
            items.find((i) => i.id === showTagsFolderDialog)?.folderId
          }
          open={showTagsFolderDialog !== null}
          onOpenChange={(open) => {
            if (!open) setShowTagsFolderDialog(null);
          }}
          onSuccess={() => {
            setShowTagsFolderDialog(null);
            onRefresh();
          }}
        />
      )}

      {/* Alert dialog pour confirmer la duplication */}
      <AlertDialog open={duplicateConfirmOpen} onOpenChange={setDuplicateConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("poll_actions.duplicate_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-foreground">
                    {pollToDuplicate?.title}
                  </p>
                </div>

                {/* Infos du poll */}
                <div className="text-sm text-muted-foreground space-y-2">
                  {pollToDuplicate?.type === "date" && (
                    <>
                      {(pollToDuplicate as any).selectedDates && (
                        <p>
                          📅 {(pollToDuplicate as any).selectedDates.length}{" "}
                          {t("poll_actions.duplicate_info_dates")}
                        </p>
                      )}
                      {(pollToDuplicate as any).timeSlotsByDate &&
                        Object.keys(
                          (pollToDuplicate as any).timeSlotsByDate
                        ).length > 0 && (
                          <p>
                            ⏰ {t("poll_actions.duplicate_info_timeslots")}
                          </p>
                        )}
                    </>
                  )}

                  {pollToDuplicate?.type === "form" && (
                    <p>
                      📝 {t("poll_actions.duplicate_type_form")}
                    </p>
                  )}

                  {pollToDuplicate?.type === "quizz" && (
                    <p>
                      ❓ {t("poll_actions.duplicate_type_quizz")}
                    </p>
                  )}
                </div>

                {/* Warning sur les votes */}
                <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>⚠️ {t("poll_actions.duplicate_votes_warning")}</strong>
                  </p>
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                    {t("poll_actions.duplicate_votes_warning_desc")}
                  </p>
                </div>

                {/* Info conversation créée */}
                {pollToDuplicate?.type === "date" && (
                  <p className="text-xs text-muted-foreground">
                    ℹ️ {t("poll_actions.duplicate_conversation_info")}
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex gap-2 justify-end">
            <AlertDialogCancel>
              {t("poll_actions.duplicate_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDuplicate}>
              {t("poll_actions.duplicate_confirm")}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
